import { faEdit, faInfoCircle, faLayerGroup, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import useModalManager from '@hooks/useModalManager';
import { Badge, Blockquote, Button, Card } from '@ui';
import Dropdown from '@ui/Dropdown';
import Tooltip from '@ui/Tooltip';
import { useClubSlug } from 'components/Clubs/hooks';
import { FormAssistent } from 'components/Formular/FormAssistent';
import type { FormField } from 'components/Formular/FormAssistent.types';
import Loader from 'components/Loader';
import { useApi } from 'hooks/useApi';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import SelectComponent from '../SelectComponent';

interface MigrationForm {
    id: number;
    name: string;
    fields: FormField[];
    usage_summary: string;
    suggested_club_ids: number[];
}

interface Club {
    id: number;
    title: string;
    slug: string;
}

interface Props {
    onDone: () => void;
}

export const FormularMigrationModal = ({ onDone }: Props) => {
    const api = useApi();
    const clubSlug = useClubSlug();
    const { addModal, removeModal } = useModalManager();
    const {
        data: statusData,
        error: statusError,
        mutate: mutateStatus
    } = useSWR<{
        needs_migration: boolean;
        forms: MigrationForm[];
    }>('user/formular/migration-status');
    const { data: clubsData } = useSWR<{ data: Club[] }>('user/clubs');

    const forms: MigrationForm[] = statusData?.forms ?? [];
    const clubs: Club[] = clubsData?.data ?? [];
    const [assignments, setAssignments] = useState<Record<number, number[]>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [sortMode, setSortMode] = useState<'default' | 'unassigned' | 'assigned'>('default');

    const resolvedAssignments = (formId: number, prev = assignments): number[] => {
        if (prev[formId] !== undefined) return prev[formId];
        const form = forms.find((f) => f.id === formId);
        const knownClubIds = new Set(clubs.map((c) => c.id));
        const suggested = (form?.suggested_club_ids ?? []).map(Number).filter((id) => knownClubIds.has(id));
        if (suggested.length === 0 && clubs.length === 1) return [clubs[0].id];
        return suggested;
    };

    const addClub = (formId: number, clubId: number) => {
        setAssignments((prev) => ({
            ...prev,
            [formId]: [...new Set([...resolvedAssignments(formId, prev), clubId])]
        }));
    };

    const removeClub = (formId: number, clubId: number) => {
        setAssignments((prev) => ({
            ...prev,
            [formId]: resolvedAssignments(formId, prev).filter((id) => id !== clubId)
        }));
    };

    const assignAllClubsToForm = (formId: number) => {
        setAssignments((prev) => ({
            ...prev,
            [formId]: clubs.map((c) => c.id)
        }));
    };

    const bulkAddClubToUnassigned = (clubId: number) => {
        setAssignments((prev) => {
            const next = { ...prev };
            forms.forEach((f) => {
                if (resolvedAssignments(f.id, prev).length === 0) {
                    next[f.id] = [clubId];
                }
            });
            return next;
        });
    };

    const bulkAddClubToAll = (clubId: number) => {
        setAssignments((prev) => {
            const next = { ...prev };
            forms.forEach((f) => {
                const current = resolvedAssignments(f.id, prev);
                if (!current.includes(clubId)) {
                    next[f.id] = [...current, clubId];
                }
            });
            return next;
        });
    };

    const clearAll = useConfirmedCallback(() => setAssignments(Object.fromEntries(forms.map((f) => [f.id, []]))));

    const isSuggested = (formId: number, clubId: number) => {
        const form = forms.find((f) => f.id === formId);
        return (form?.suggested_club_ids ?? []).map(Number).includes(clubId);
    };

    const allAssigned = forms.every((f) => resolvedAssignments(f.id).length > 0);

    const handleMigrate = () => {
        setIsSubmitting(true);
        setSubmitError(null);
        api({
            url: 'user/formular/migrate',
            method: 'POST',
            data: {
                assignments: forms.map((f) => ({
                    form_id: f.id,
                    club_ids: resolvedAssignments(f.id)
                }))
            },
            onSuccess: () => onDone(),
            onError: (error) => {
                const message =
                    error?.response?.data?.message ||
                    error?.message ||
                    'Migration fehlgeschlagen. Bitte erneut versuchen.';
                setSubmitError(message);
            },
            onFinish: () => setIsSubmitting(false)
        });
    };

    const isLoading = !statusData && !statusError;
    const assignedCount = forms.filter((f) => resolvedAssignments(f.id).length > 0).length;
    const unassignedCount = forms.length - assignedCount;

    const formsMissingSuggested = forms
        .map((f) => {
            const assigned = new Set(resolvedAssignments(f.id));
            const missing = (f.suggested_club_ids ?? [])
                .map(Number)
                .filter((id) => clubs.some((c) => c.id === id) && !assigned.has(id));
            return missing.length > 0 ? { form: f, missingClubIds: missing } : null;
        })
        .filter((x): x is { form: MigrationForm; missingClubIds: number[] } => x !== null);

    useEffect(() => {
        if (sortMode === 'unassigned' && unassignedCount === 0) {
            setSortMode('default');
        }
    }, [sortMode, unassignedCount]);

    const visibleForms =
        sortMode === 'unassigned'
            ? forms.filter((f) => resolvedAssignments(f.id).length === 0)
            : sortMode === 'assigned'
              ? forms.filter((f) => resolvedAssignments(f.id).length > 0)
              : forms;
    const openEditForm = (form: MigrationForm) => {
        addModal({
            title: 'Formular bearbeiten',
            content: (id) => (
                <FormAssistent
                    clubSlug={clubSlug}
                    form={form}
                    cb={() => {
                        removeModal(id);
                        void mutateStatus();
                    }}
                />
            ),
            size: 100
        });
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-pb-2">
                <p className="tw-my-0 tw-text-xs tw-leading-5 tw-text-accents-7">
                    Vorschläge prüfen, fehlende Clubs ergänzen, dann migrieren. Mehrere Clubs erzeugen automatisch
                    separate Kopien.
                </p>

                <div className="tw-flex tw-items-center tw-gap-1.5">
                    {(
                        [
                            { label: 'Alle', value: 'default', count: forms.length },
                            { label: 'Offen', value: 'unassigned', count: unassignedCount },
                            { label: 'Zugewiesen', value: 'assigned', count: assignedCount }
                        ] as const
                    ).map(({ label, value, count }) => {
                        const disabled = value !== 'default' && count === 0;
                        return (
                            <Button
                                key={value}
                                onPress={() => setSortMode(value)}
                                flat={sortMode !== value}
                                color={sortMode === value ? 'default' : 'light'}
                                size="xs"
                                disabled={disabled}
                            >
                                {label} ({count})
                            </Button>
                        );
                    })}
                </div>
            </div>

            {forms.length > 0 && clubs.length > 1 && (
                <Card className="tw-border-0 tw-bg-transparent tw-p-0 tw-shadow-none">
                    <div className="tw-flex tw-flex-col tw-gap-1.5">
                        <div className="tw-flex tw-w-full tw-flex-nowrap tw-items-center tw-gap-1.5">
                            <div className="tw-min-w-0 tw-flex-1">
                                <SelectComponent
                                    value={undefined}
                                    onChange={(value) => bulkAddClubToUnassigned(Number(value))}
                                    buttonProps={{
                                        label: 'Nur offenen Formularen zuweisen…',
                                        flat: true,
                                        color: 'light',
                                        size: 'xs',
                                        block: true
                                    }}
                                    options={clubs.map((c) => ({ label: c.title, value: c.id }))}
                                />
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                <SelectComponent
                                    value={undefined}
                                    onChange={(value) => bulkAddClubToAll(Number(value))}
                                    buttonProps={{
                                        label: 'Bei allen Formularen ergänzen…',
                                        flat: true,
                                        color: 'light',
                                        size: 'xs',
                                        block: true
                                    }}
                                    options={clubs.map((c) => ({ label: c.title, value: c.id }))}
                                />
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                <Button size="xs" color="light" flat block onPress={clearAll}>
                                    Alle Zuweisungen zurücksetzen
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {statusError && (
                <Blockquote color="error">
                    <div className="tw-text-xs tw-leading-5">
                        Status konnte nicht geladen werden. Bitte aktualisiere die Seite oder versuche es erneut.
                    </div>
                </Blockquote>
            )}

            <Loader isLoading={isLoading}>
                <div className="tw-flex tw-flex-col tw-gap-2">
                    {forms.length === 0 && (
                        <Card className="tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-p-4 tw-text-center">
                            <div className="tw-text-sm tw-font-semibold tw-text-accents-9">
                                Keine Formulare gefunden
                            </div>
                            <div className="tw-mt-1 tw-text-xs tw-text-accents-7">
                                Aktuell gibt es nichts zu migrieren.
                            </div>
                        </Card>
                    )}

                    {forms.some((f) => resolvedAssignments(f.id).length === 0) && (
                        <Blockquote color="warning">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <div className="tw-text-xs tw-leading-5">
                                    Mindestens ein Formular braucht noch eine Club-Zuweisung.
                                </div>
                                <Badge color="warning" size="xs">
                                    {unassignedCount} offen
                                </Badge>
                            </div>
                        </Blockquote>
                    )}

                    {visibleForms.map((form) => {
                        const clubIds = resolvedAssignments(form.id);
                        const unassigned = clubIds.length === 0;
                        const isMultiClub = clubIds.length > 1;
                        return (
                            <Card
                                key={form.id}
                                className={`tw-rounded-lg tw-border tw-border-solid tw-p-3 tw-transition-colors ${
                                    unassigned
                                        ? 'tw-border-orange-200 tw-bg-orange-50/40'
                                        : 'tw-border-accents-3 tw-bg-white'
                                }`}
                            >
                                <div className="tw-grid tw-gap-2 md:tw-grid-cols-[minmax(0,1fr)_190px] md:tw-items-start">
                                    <div className="tw-space-y-2">
                                        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
                                            <div className="tw-min-w-0 tw-flex-1">
                                                <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-accents-9">
                                                    {form.name}
                                                </div>
                                                <div className="tw-mt-0.5 tw-text-xs tw-leading-5 tw-text-accents-7">
                                                    {form.usage_summary}
                                                </div>
                                            </div>

                                            <Badge flat color={unassigned ? 'warning' : 'light'} size="xs">
                                                {unassigned
                                                    ? 'Zuweisung erforderlich'
                                                    : isMultiClub
                                                      ? `${clubIds.length} Clubs`
                                                      : '1 Club'}
                                            </Badge>
                                        </div>

                                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                                            {clubIds.map((clubId) => {
                                                const club = clubs.find((c) => c.id === clubId);
                                                if (!club) return null;
                                                const suggested = isSuggested(form.id, clubId);
                                                return (
                                                    <Badge
                                                        key={clubId}
                                                        className={`tw-flex tw-items-center tw-gap-1.5 !tw-px-0.5 ${
                                                            suggested
                                                                ? '!tw-border !tw-border-dashed !tw-border-accents-4'
                                                                : ''
                                                        }`}
                                                        size="xs"
                                                        color="light"
                                                    >
                                                        <span className="tw-text-xs">{club.title}</span>
                                                        {suggested && (
                                                            <Tooltip
                                                                tooltip={
                                                                    <span>
                                                                        Vorschlag basierend auf bisheriger Nutzung.
                                                                    </span>
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faInfoCircle}
                                                                    size="lg"
                                                                    className="tw-text-accents-6"
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        <Button
                                                            onPress={() => removeClub(form.id, clubId)}
                                                            size="xs"
                                                            color="error"
                                                            className="!tw-h-4 !tw-w-4 !tw-min-w-0 !tw-rounded-full !tw-p-0"
                                                            aria-label={`${club.title} entfernen`}
                                                        >
                                                            <FontAwesomeIcon size="sm" icon={faTimes} />
                                                        </Button>
                                                    </Badge>
                                                );
                                            })}

                                            {isMultiClub && (
                                                <Tooltip
                                                    tooltip={
                                                        <span className="tw-block tw-max-w-[240px] tw-text-[13px] tw-leading-4">
                                                            Pro Club entsteht eine eigene Kopie. Bestehende Einsendungen
                                                            werden dabei dem zuerst genannten Club zugeordnet.
                                                        </span>
                                                    }
                                                >
                                                    <span className="tw-flex tw-cursor-default tw-items-center tw-gap-1 tw-text-xs tw-text-accents-6">
                                                        Wird pro Club als eigene Kopie erstellt.
                                                        <FontAwesomeIcon
                                                            icon={faInfoCircle}
                                                            className="tw-text-accents-5"
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="tw-flex tw-items-center tw-gap-1.5 md:tw-justify-end">
                                        <Tooltip flat withArrow placement="top" tooltip="Formular ansehen">
                                            <Button
                                                icon={<FontAwesomeIcon icon={faEdit} />}
                                                size="xs"
                                                color="light"
                                                flat
                                                aria-label="Formular ansehen"
                                                onPress={() => openEditForm(form)}
                                            />
                                        </Tooltip>

                                        <Tooltip flat withArrow placement="top" tooltip="Club hinzufügen">
                                            <Dropdown
                                                slim
                                                icon={<FontAwesomeIcon icon={faPlus} />}
                                                size="xs"
                                                flat
                                                color="light"
                                            >
                                                {clubs
                                                    .filter((c) => !clubIds.includes(c.id))
                                                    .map((c) => (
                                                        <Dropdown.Item
                                                            key={c.id}
                                                            label={c.title}
                                                            onClick={() => addClub(form.id, c.id)}
                                                        />
                                                    ))}
                                            </Dropdown>
                                        </Tooltip>

                                        {clubs.length > 1 && clubIds.length < clubs.length && (
                                            <Tooltip flat withArrow placement="top" tooltip="Allen Clubs zuweisen">
                                                <Button
                                                    icon={<FontAwesomeIcon icon={faLayerGroup} />}
                                                    size="xs"
                                                    color="light"
                                                    flat
                                                    aria-label="Allen Clubs zuweisen"
                                                    onPress={() => assignAllClubsToForm(form.id)}
                                                />
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </Loader>

            <div className="tw-mt-2">
                {submitError && (
                    <Blockquote color="error" className="tw-mb-2">
                        <div className="tw-text-xs tw-leading-5">{submitError}</div>
                    </Blockquote>
                )}
                {formsMissingSuggested.length > 0 && (
                    <Blockquote color="warning" className="tw-mb-2">
                        <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-leading-5">
                            <div className="tw-font-semibold">
                                {formsMissingSuggested.length === 1
                                    ? '1 Formular wird in einem Club genutzt, aber nicht zugewiesen.'
                                    : `${formsMissingSuggested.length} Formulare werden in Clubs genutzt, aber nicht zugewiesen.`}
                            </div>
                            <div className="tw-text-accents-7">
                                Diese Verknüpfungen gehen nach der Migration verloren. Du kannst trotzdem fortfahren,
                                wenn das so gewollt ist.
                            </div>
                            <ul className="tw-mt-1 tw-space-y-0.5 tw-pl-3">
                                {formsMissingSuggested.slice(0, 5).map(({ form, missingClubIds }) => {
                                    const missingNames = missingClubIds
                                        .map((id) => clubs.find((c) => c.id === id)?.title)
                                        .filter(Boolean)
                                        .join(', ');
                                    return (
                                        <li key={form.id} className="tw-list-disc">
                                            <span className="tw-font-medium">{form.name}</span>
                                            <span className="tw-text-accents-7"> – {missingNames}</span>
                                        </li>
                                    );
                                })}
                                {formsMissingSuggested.length > 5 && (
                                    <li className="tw-list-disc tw-text-accents-7">
                                        … und {formsMissingSuggested.length - 5} weitere
                                    </li>
                                )}
                            </ul>
                        </div>
                    </Blockquote>
                )}
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                    <div>
                        <div className="tw-text-sm tw-font-semibold tw-text-accents-9">
                            {assignedCount} von {forms.length} Formularen bereit
                        </div>
                        <div className="tw-mt-0.5 tw-text-xs tw-leading-5 tw-text-accents-7">
                            {!allAssigned
                                ? 'Ergänze die fehlenden Zuweisungen, um die Migration freizuschalten.'
                                : 'Alle Formulare sind zugewiesen und können jetzt migriert werden.'}
                        </div>
                    </div>
                    <Button
                        disabled={!allAssigned || isSubmitting || forms.length === 0}
                        onPress={handleMigrate}
                        color="default"
                        size="sm"
                    >
                        {isSubmitting ? 'Migration wird vorbereitet…' : 'Migration starten'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
